/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable no-unused-vars */
import { Input, Row, Col, Badge, Typography, Empty, Divider, Tag } from 'antd';
import PropTypes from 'prop-types';
import { SingleMultiSelectDropDown } from '../../components/Input/InputText';
import {
  Search,
  Activity,
  Droplet,
  Shield,
  Box,
  Zap,
  Video,
  Target,
  Clipboard,
  AlertCircle,
  FileText,
  Layers,
  CheckCircle
} from 'react-feather';
import { useState, useMemo } from 'react';

const { Title, Text } = Typography;

const CATEGORY_ICONS = {
  'DIGITAL X-RAY': <Activity size={18} />,
  'ULTRA SONOGRAPHY': <Video size={18} />,
  'HEMATOLOGY': <Droplet size={18} />,
  'CLINICAL PATHOLOGY': <Box size={18} />,
  'SEROLOGY AND IMMUNOLOGY': <Shield size={18} />,
  'BIOCHEMISTRY': <Zap size={18} />,
  'ENDOCRINOLOGY': <Target size={18} />,
  'HIGH-END MRI STUDY': <Layers size={18} />,
  'CT SCAN': <Zap size={18} />,
  'COLOUR DOPPLER STUDY': <Activity size={18} />,
  'HEPATITIS MARKER': <Shield size={18} />,
  'HISTOPATHOLOGY & CYTOLOGY': <Clipboard size={18} />,
  'PANELS AND PROLIFES': <Clipboard size={18} />,
  'ONCOLOGY': <AlertCircle size={18} />,
  'DRUGS & OTHERS': <Box size={18} />,
  'CHROMOSOMAL STUDY': <Activity size={18} />,
  'MICROBIOLOGY': <Activity size={18} />,
};

const getIcon = (category) => CATEGORY_ICONS[category.toUpperCase()] || <FileText size={18} />;

export const LabInvestigationComponent = ({
  errors,
  touched,
  values,
  laboratory,
  setValues,
  handleChange,
  handleBlur,
  name,
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);

  // Group laboratory data by category dynamically
  const groupedData = useMemo(() => {
    if (!laboratory || !laboratory[0]) return {};
    const data = laboratory[0];
    const grouped = {};

    data.forEach(item => {
      const category = item.category || 'OTHER';
      if (!grouped[category]) {
        grouped[category] = [];
      }

      if (!searchTerm ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.toLowerCase().includes(searchTerm.toLowerCase())) {
        grouped[category].push(item);
      }
    });

    // Remove empty categories if searching
    Object.keys(grouped).forEach(cat => {
      if (grouped[cat].length === 0) delete grouped[cat];
    });

    return grouped;
  }, [laboratory, searchTerm]);

  const sortedCategories = useMemo(() => Object.keys(groupedData).sort(), [groupedData]);

  const handleSelectChange = (NameOfField, CategoryOfField, value) => {
    let laboratoryData = [...(values?.laboratory || [])];
    let existingIndex = laboratoryData.findIndex(val => val?.name === NameOfField);

    if (existingIndex !== -1) {
      if (value && value.length > 0) {
        laboratoryData[existingIndex] = { ...laboratoryData[existingIndex], options: value };
      } else {
        laboratoryData.splice(existingIndex, 1);
      }
    } else if (value && value.length > 0) {
      laboratoryData.push({ category: CategoryOfField, name: NameOfField, options: value });
    }

    handleChange({ target: { name, value: laboratoryData } });
  };

  const selectedCount = values?.laboratory?.length || 0;

  // Custom Category IDs for smooth jump
  const getCatId = (name) => `cat-${name.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className="lab-investigation-layout" style={{ background: '#fff', borderRadius: '16px' }}>

      <div className="lab-sticky-header" style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: '#fff',
        padding: '20px',
        borderBottom: '1px solid #f1f5f9',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <Input
              prefix={<Search size={18} color="#94a3b8" />}
              placeholder="Start typing test name or category (e.g. 'MRI', 'Blood', 'X-Ray')..."
              allowClear
              size="large"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="lab-search-input"
              style={{ borderRadius: '12px' }}
            />
          </Col>
          <Col>
            <Badge count={selectedCount} showZero color="#2E37A4">
              <Tag style={{ padding: '8px 16px', borderRadius: '8px', fontSize: '14px', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#1e293b', fontWeight: 600 }}>
                Selected Findings
              </Tag>
            </Badge>
          </Col>
        </Row>

        {/* Quick Category Jump (Horizontal Scrollable) */}
        {!searchTerm && sortedCategories.length > 0 && (
          <div style={{ display: 'flex', overflowX: 'auto', gap: '8px', marginTop: '16px', paddingBottom: '8px', scrollbarWidth: 'none' }}>
            {sortedCategories.map(cat => (
              <a key={cat} href={`#${getCatId(cat)}`} style={{ textDecoration: 'none' }}>
                <Tag style={{
                  cursor: 'pointer',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  background: '#f1f5f9',
                  border: 'none',
                  color: '#475569',
                  whiteSpace: 'nowrap',
                  fontWeight: 500
                }}>
                  {cat}
                </Tag>
              </a>
            ))}
          </div>
        )}
      </div>

      <div className="lab-content-scroll" style={{ padding: '24px' }}>
        {sortedCategories.length > 0 ? (
          sortedCategories.map((category) => (
            <div key={category} id={getCatId(category)} style={{ marginBottom: '48px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px',
                padding: '4px 8px',
                background: '#f8fafc',
                borderRadius: '8px',
                width: 'fit-content'
              }}>
                <span style={{ color: '#2E37A4' }}>{getIcon(category)}</span>
                <Title level={5} style={{ margin: 0, letterSpacing: '0.05em', color: '#334155' }}>
                  {category.toUpperCase()}
                </Title>
                <Badge count={groupedData[category].length} style={{ backgroundColor: '#e2e8f0', color: '#64748b', boxShadow: 'none' }} />
              </div>

              {/* Dynamic Grid for Investigations - Handles varying number of questions gracefully */}
              <Row gutter={[24, 24]}>
                {groupedData[category].map((test) => {
                  const isSelected = !!values?.laboratory?.find(e => e.name === test.name);
                  return (
                    <Col xs={24} sm={12} key={test.name}>
                      <div className={`lab-test-item ${isSelected ? 'selected' : ''}`} style={{
                        padding: '16px',
                        background: isSelected ? 'rgba(79, 70, 229, 0.03)' : '#fff',
                        borderRadius: '12px',
                        border: `1.5px solid ${isSelected ? '#2E37A4' : '#f1f5f9'}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <Text strong style={{ fontSize: '15px', color: isSelected ? '#2E37A4' : '#1e293b' }}>
                            {test.name}
                          </Text>
                          {isSelected && <CheckCircle size={16} color="#10B981" fill="#10B98122" />}
                        </div>

                        <div className="lab-dropdown-wrapper">
                          <SingleMultiSelectDropDown
                            Showlabel={false}
                            label={test.name}
                            type={test.name}
                            FormGroupClassName={'mb-0'}
                            name={test.name}
                            disabled={disabled}
                            onChange={value => handleSelectChange(test.name, test.category, value)}
                            FormControlClassName={'form-control border-0 bg-transparent'}
                            onBlur={() => handleBlur({ target: { name: test.name, value: true } })}
                            value={values?.laboratory?.find(e => e.name === test.name)?.options || []}
                            options={test.options}
                            touched={touched?.[test.name]}
                            placeholder={`+ Add findings for ${test.name.toLowerCase()}...`}
                            Dropdown_ClassName="modern-select"
                          />
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
              <Divider style={{ margin: '32px 0 0 0' }} />
            </div>
          ))
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div style={{ color: '#94a3b8' }}>
                <p style={{ fontSize: '16px', fontWeight: 500, margin: 0 }}>No matching investigations</p>
                <p style={{ fontSize: '13px' }}>Try searching with a broader term...</p>
              </div>
            }
          />
        )}

        {/* Notes Section - Clean & Simple */}
        <div style={{ marginTop: '40px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <FileText size={20} color="#2E37A4" />
            <Text strong style={{ fontSize: '16px' }}>Clinical Notes / Special Instructions</Text>
          </div>
          <textarea
            rows={10}
            className="form-control"
            placeholder="Add relevant patient history or specific clinical queries for the lab..."
            disabled={disabled}
            style={{
              borderRadius: '16px',
              padding: '16px',
              border: errors?.laboratoryNote && touched?.laboratoryNote ? '1.5px solid #ef4444' : '1.5px solid #e2e8f0',
              fontSize: '14px',
              background: '#f8fafc',
              resize: 'none',
              boxShadow: 'none'
            }}
            value={values?.laboratoryNote}
            onBlur={() => handleBlur({ target: { name: 'laboratoryNote', value: true } })}
            onChange={e => handleChange({ target: { name: 'laboratoryNote', value: e.target.value } })}
          />
          {errors?.laboratoryNote && touched?.laboratoryNote && (
            <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <AlertCircle size={14} /> {errors.laboratoryNote}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .lab-sticky-header {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
        }
        .lab-search-input:hover, .lab-search-input:focus {
          border-color: #2E37A4 !important;
          box-shadow: 0 0 0 2px rgba(46, 55, 164, 0.1) !important;
        }
        .lab-test-item:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }
        .lab-test-item.selected {
          box-shadow: 0 4px 12px rgba(46, 55, 164, 0.08);
        }
        .modern-select .ant-select-selector {
          padding-left: 0 !important;
          background: transparent !important;
        }
        .lab-investigation-layout ::-webkit-scrollbar {
          height: 0px;
        }
        @media (max-width: 768px) {
          .lab-content-scroll { padding: 16px; }
          .lab-sticky-header { padding: 16px; }
        }
      `}</style>
    </div>
  );
};

LabInvestigationComponent.propTypes = {
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  laboratory: PropTypes.array,
  setValues: PropTypes.func,
  handleBlur: PropTypes.func,
  handleChange: PropTypes.func,
  values: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};
